import { useState } from 'react';
import type { ServerSetting } from '../../../services/adminServerSettings';
import type { ImageSizing } from '../../../services/adminUnits';
import CurrentImageList from './CurrentImageList';
import EndedUnits from './EndedUnits';
import Figure from './UnitFigure';
import { formatCpuAveragePeak, formatGb, formatMemoryAveragePeak, plural } from './unitsFormat';
import { UnitsCpuCoresKey, UnitsMemoryGbKey, appliedValue, currentImageTag } from './unitsPolicy';

const sizeLabel = (cores: number | string, memoryGb: number | string) => `${cores} cores, ${memoryGb} GB`;

function SizingRow({ sizing }: { sizing: ImageSizing }) {
  const [unitsShown, setUnitsShown] = useState(false);
  const size = sizeLabel(sizing.cpuCores, sizing.memoryGb);
  return (
    <div
      role="listitem"
      aria-label={`${sizing.imageTag}, ${size}`}
      className="card"
      style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span className="mono" style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: 'anywhere' }}>{sizing.imageTag}</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {size} · {plural(sizing.units, 'unit', 'units')} · {plural(sizing.usage.samples, 'sample', 'samples')}
        </span>
        <button
          type="button"
          className="btn ghost sm"
          aria-expanded={unitsShown}
          onClick={() => setUnitsShown(!unitsShown)}
          style={{ marginLeft: 'auto', height: 28, padding: '0 10px' }}
        >
          {unitsShown ? 'Hide units' : 'Units'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <Figure label="CPU avg / peak" value={formatCpuAveragePeak(sizing.usage)} />
        <Figure label="Memory avg / peak" value={formatMemoryAveragePeak(sizing.usage)} />
        <Figure label="Free disk min" value={formatGb(sizing.usage.freeDiskMinBytes)} />
      </div>
      {unitsShown && <EndedUnits imageTag={sizing.imageTag} cpuCores={sizing.cpuCores} memoryGb={sizing.memoryGb} />}
    </div>
  );
}

interface UnitsSizingProps {
  sizing: ImageSizing[];
  /** Null until loaded; the policy's size and the current image tag come from them. */
  settings: ServerSetting[] | null;
}

export default function UnitsSizing({ sizing, settings }: UnitsSizingProps) {
  const cores = settings && appliedValue(settings, UnitsCpuCoresKey);
  const memoryGb = settings && appliedValue(settings, UnitsMemoryGbKey);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
      {cores && memoryGb && <div>Policy: {sizeLabel(cores, memoryGb)}</div>}
      {sizing.length === 0 ? (
        <div className="card quiet" style={{ padding: 16 }}>
          <span className="muted">No ended unit reported usage in the last 7 days.</span>
        </div>
      ) : (
        <CurrentImageList
          items={sizing}
          currentTag={currentImageTag(settings)}
          tagOf={(row) => row.imageTag}
          renderItem={(row) => <SizingRow key={`${row.imageTag} ${row.cpuCores} ${row.memoryGb}`} sizing={row} />}
          label="Sizing per image"
          olderLabel="Sizing of older images"
          noCurrent={<div className="muted">No ended units on the current image yet.</div>}
        />
      )}
    </div>
  );
}
